<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { track } from "jiffy-analytics";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";

const props = defineProps({
  lineItem: {
    type: Object,
    required: true
  },
  variant: {
    type: Object,
    required: true
  },
  selectedColor: {
    type: Object,
    default: null
  },
  effectiveTier: {
    type: Number,
    default: 0
  }
});
const emit = defineEmits(["input", "validate"]);

const { variant } = props;
const field = ref(null);
const fieldValue = ref(variant.quantity || 0);
const invalid = ref(false);

const oos = computed(() => variant.inventory === 0);

const basePrice = computed(() =>
  variant.price_tiers ? parseFloat(variant.price_tiers["0"]) || 0 : 0
);

const priceFromTier = computed(() => {
  const tierPrice = variant.price_tiers?.[props.effectiveTier]
    ? parseFloat(variant.price_tiers[props.effectiveTier])
    : parseFloat(variant.price_tiers?.["0"] || 0);
  return tierPrice;
});

const stock = computed(() => {
  const inventoryValue = Number(variant.inventory) || 0;
  const quantityValue = Number(variant.quantity) || 0;
  const inventory = Math.max(0, inventoryValue - quantityValue);

  if (inventory > 999) {
    return "999+";
  }

  return inventory;
});

const validate = () => {
  let errorMessage = null;
  const inventory = variant.inventory === 999 ? Infinity : variant.inventory;
  const quantity = Number(variant.quantity) || 0;

  if (fieldValue.value > 0 && fieldValue.value < props.lineItem.minQuantity) {
    errorMessage = `Minimum quantity is ${props.lineItem.minQuantity}`;
  } else if (Number.isNaN(Number(fieldValue.value))) {
    errorMessage = "Please enter a valid quantity";
  } else if (fieldValue.value > inventory - quantity) {
    errorMessage = "Quantity exceeds available stock";
  }

  invalid.value = errorMessage !== null;
  emit("validate", {
    message: errorMessage
  });
};

const updateFieldValue = value => {
  let parsedValue = String(value)
    .replace(/[^0-9]/g, "")
    .replace(/^0/, "");

  parsedValue = Number(parsedValue);
  if (Number.isNaN(parsedValue) || parsedValue < 0) {
    parsedValue = 0;
  } else if (parsedValue > 9999) {
    parsedValue = 9999;
  }

  fieldValue.value = parsedValue;
  field.value.value = parsedValue;

  emit("input", {
    id: variant.id,
    price: priceFromTier.value,
    quantity: parsedValue,
    size: variant.size_label,
    basePrice: basePrice.value
  });

  validate();
};

const clearField = () => {
  fieldValue.value = 0;
  field.value.value = 0;

  emit("input", {
    id: variant.id,
    price: priceFromTier.value,
    quantity: 0,
    size: variant.size_label,
    basePrice: basePrice.value
  });
};

onMounted(() => {
  variant.quantity && updateFieldValue(variant.quantity);
});

watch(
  () => fieldValue.value,
  (newValue, oldValue) => {
    const trackData = {
      color: props.selectedColor?.colorName || props.lineItem.productColor,
      name: props.lineItem.productName,
      price: priceFromTier.value,
      quantity: newValue,
      size: variant.size_label
    };

    if (newValue > oldValue) {
      track("cart_size_increase", trackData);
    } else if (newValue < oldValue) {
      track("cart_size_decrease", trackData);
    }
  }
);
</script>

<template>
  <div class="size-quantity-control">
    <div class="size-quantity-control__label-wrap">
      <label class="size-quantity-control__label">{{ variant.size_label }}</label>
      <button
        :disabled="fieldValue <= 0"
        type="button"
        class="size-quantity-control__clear-button"
        @click="clearField"
      >
        Clear
      </button>
    </div>

    <div class="size-quantity-control__input-wrap">
      <input
        ref="field"
        :disabled="oos"
        :value="fieldValue"
        class="size-quantity-control__input"
        :class="{
          'size-quantity-control__input--empty': fieldValue === 0,
          'size-quantity-control__input--invalid': invalid
        }"
        maxlength="4"
        pattern="^[0-9]+$"
        type="tel"
        @input="updateFieldValue($event.target.value)"
      />

      <button
        v-if="!oos"
        :disabled="fieldValue === 0"
        class="size-quantity-control__button size-quantity-control__button--minus"
        type="button"
        @click="updateFieldValue(fieldValue - 1)"
      >
        <UseSvg class="size-quantity-control__button-icon" icon="minus" />
      </button>
      <button
        v-if="!oos"
        :disabled="fieldValue === 9999"
        class="size-quantity-control__button size-quantity-control__button--plus"
        type="button"
        @click="updateFieldValue(fieldValue + 1)"
      >
        <UseSvg class="size-quantity-control__button-icon" icon="plus" />
      </button>
      <UseSvg v-if="oos" class="size-quantity-control__cross-icon" icon="cross-thin" />
    </div>

    <div v-if="oos" class="size-quantity-control__out-of-stock">Out of Stock</div>
    <div v-else class="size-quantity-control__stock">
      <span class="size-quantity-control__stock-label">Stock:</span>
      <span class="size-quantity-control__stock-quantity">{{ stock }}</span>
    </div>

    <div v-if="!oos" class="size-quantity-control__price">
      <span class="size-quantity-control__price-value">
        {{ n(priceFromTier, "currency") }}
      </span>
    </div>
  </div>
</template>

<style lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/button/mixin.scss";

.size-quantity-control {
  width: 100%;
  text-align: center;

  &__label-wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 3px;
  }

  &__label {
    display: block;
    color: $text-black;
    font-size: 14px;
    font-weight: 700;
  }

  &__input-wrap {
    position: relative;
  }

  &__input {
    width: 100%;
    height: 34px;
    border: 1px solid $gray-border;
    border-radius: 4px;
    color: $text-black;
    font-size: 14px;
    font-weight: 700;
    text-align: center;

    &:disabled {
      background-color: lighten($blue-lightest, 2%);
      color: $gray-label;
    }

    &--empty {
      color: $gray-label;
    }

    &--invalid {
      border-color: $red;
      outline-color: $red;
    }
  }

  &__button {
    @include clear-button;
    @include svg-wrapper;

    position: absolute;
    top: 3px;
    width: 26px;
    height: 26px;
    padding: 4px;
    color: $text-black;

    &:disabled {
      color: $gray-label;
    }

    &--minus {
      left: 2px;
    }

    &--plus {
      right: 2px;
    }
  }

  &__clear-button {
    position: static;
    color: rgba($text-black, 0.75);
    font-size: 12px;
    text-decoration: underline;
    background-color: transparent;
    border: none;
    cursor: pointer;
    width: 30px;
    height: 26px;
    padding: 0;

    &:disabled {
      color: $gray-label;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__cross-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 16px;
    height: 16px;
    transform: translate(-50%, -50%);
    color: $text-black;
  }

  &__out-of-stock {
    color: $gray-chateau;
    font-size: 11px;
  }

  &__stock {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
  }

  &__stock-label {
    color: $warm-grey;
  }

  &__stock-quantity {
    color: $feedback-success;
    font-weight: 700;
  }

  &__price-value {
    color: $black;
    font-size: 16px;
    font-weight: 700;
  }
}
</style>
